import { Flex, Text, Switch } from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import {
  GetAvailableFields,
  GetCostPositionEmployeeFieldGroups,
  initializeFieldFormats,
  MAPPING_TEST_PREFIX
} from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/MappingConfiguration/Utils";
import { PositionConfig } from "@coreapp/CoreApp/PositionConfig";
import { CostBandFieldMappingRow } from "./CostBandFieldMappingRow";
import {
  CustomTooltip,
  TOOLTIP_FORMATS
} from "@/appComponents/Blocks/Composites/CustomTooltip/CustomTooltip";
import * as React from "react";
import { useEffect } from "react";
import { enumObjectTypes, SYSTEM_CALCULATE } from "@coreapp/constants";

export const CostBandConfiguration = ({
  localConfig,
  disableDropdowns,
  handleFieldRename,
  handleCustomFieldNameValidation,
  onUpdate
}) => {
  const costFieldGroups = GetCostPositionEmployeeFieldGroups();
  const { costBandGroup } = costFieldGroups || {
    costBandGroup: {}
  };

  const posConfig = new PositionConfig(localConfig);
  const useCostBand = posConfig.useCostBand();

  useEffect(() => {
    handleInitialCostBand();
  }, [localConfig]);

  const handleInitialCostBand = () => {
    const currencyFields = [];

    // Get mapped names for cost band fields
    Object.keys(costBandGroup).forEach(fieldId => {
      const mappedName = localConfig.fields?.[fieldId];
      if (mappedName && mappedName !== SYSTEM_CALCULATE && mappedName !== "") {
        currencyFields.push(mappedName);
      } else {
        currencyFields.push(fieldId);
      }
    });

    if (currencyFields.length > 0) {
      initializeFieldFormats(currencyFields, localConfig, onUpdate, "currency");
    }
  };

  const handleUseCostBandChange = nextChecked => {
    onUpdate(
      {
        positionChart: { ...localConfig.positionChart, useCostBand: nextChecked }
      },
      ["positionChart"]
    );
  };

  const handleFieldMappingChange = (option, key) => {
    let nextFieldMap = structuredClone(localConfig["fields"] ?? {});
    nextFieldMap[key] = option.type === enumObjectTypes.position ? option.header : key;

    let nextCostBandFields = structuredClone(localConfig["positionChart"]?.costBandFields) || {};
    nextCostBandFields = {
      ...nextCostBandFields,
      [key]: {
        type: option.type,
        fieldName: option.header
      }
    };

    const currentSystemSettings = localConfig.positionChart?.costBandSystem || {};
    const updatedSystemSettings = { ...currentSystemSettings };
    delete updatedSystemSettings[key];

    onUpdate(
      {
        fields: nextFieldMap,
        positionChart: {
          ...localConfig.positionChart,
          costBandFields: nextCostBandFields,
          costBandSystem: updatedSystemSettings
        }
      },
      ["fields", "positionChart"]
    );
  };

  // Cost Band fields can be position/employee
  const BuildFieldMappingOptions = fieldMapID => {
    const createOptions = (headers, type) =>
      headers.map(header => ({
        value: `${type}-${header}`,
        header,
        type,
        label: header,
        clickHandler: e => handleFieldMappingChange(e, fieldMapID)
      }));

    const positionHeaders = GetAvailableFields(localConfig, fieldMapID, false);
    const holderHeaders = GetAvailableFields(localConfig, fieldMapID, true);

    const dropdownConfigs = [
      {
        label: appstr("Employee fields"),
        value: "employeeFields",
        keepOpenOnClick: true,
        options: createOptions(holderHeaders, enumObjectTypes.holder),
        listProps: {
          enableSearch: true
        }
      },
      {
        label: appstr("Position fields"),
        value: "positionFields",
        keepOpenOnClick: true,
        options: createOptions(positionHeaders, enumObjectTypes.position),
        listProps: {
          enableSearch: true
        }
      }
    ];

    return dropdownConfigs;
  };

  const getFieldValue = fieldKey => {
    const fieldMapping = localConfig.fields?.[fieldKey];
    const isMapped = fieldMapping?.length > 0 && fieldMapping !== SYSTEM_CALCULATE;

    if (isMapped) {
      const costBandConfig = localConfig.positionChart?.costBandFields?.[fieldKey];
      const fieldType = costBandConfig?.type ?? enumObjectTypes.position;
      const fieldName = costBandConfig?.fieldName ?? localConfig.fields[fieldKey];
      return `${fieldType}-${fieldName}`;
    }
  };

  const getFieldType = fieldKey => {
    const costBandConfig = localConfig.positionChart?.costBandFields?.[fieldKey];
    return costBandConfig?.type;
  };

  return (
    <Flex direction={"column"} gap={"16px"}>
      <Flex gap={"8px"} alignItems="center">
        <div style={{ position: "relative" }}>
          <Switch
            id={"use-cost-band-switch"}
            data-testid={`${MAPPING_TEST_PREFIX}-costband-use-switch`}
            label={appstr("Use cost band data")}
            labelPlacement={"right"}
            onChange={handleUseCostBandChange}
            checked={useCostBand}
          />
        </div>
        <CustomTooltip
          messageKey="Cost band help"
          replaceDict={{ "Help Center": appstr("Help Center") }}
          formatConfig={TOOLTIP_FORMATS.helpCenterLink(
            "https://theorgchart.zendesk.com/hc/en-us/articles/38560822856724-Mapping-budget-and-compensation-fields"
          )}
        />
      </Flex>
      {useCostBand && costBandGroup && (
        <Flex direction="column" gap="16px" className="ocn-cost-band-fields">
          {Object.keys(costBandGroup).map(fieldKey => (
            <CostBandFieldMappingRow
              key={fieldKey}
              fieldKey={fieldKey}
              fieldValue={getFieldValue(fieldKey)}
              fieldType={getFieldType(fieldKey)}
              localConfig={localConfig}
              BuildFieldMappingOptions={BuildFieldMappingOptions}
              disabled={disableDropdowns}
              handleFieldRename={handleFieldRename}
              handleCustomFieldNameValidation={handleCustomFieldNameValidation}
              onUpdate={onUpdate}
            />
          ))}
        </Flex>
      )}
    </Flex>
  );
};
